// @ts-strict-ignore
import * as React from 'react';
import clsx from 'clsx';
import {Alignment, Fit, Layout, RiveState, useRive, useStateMachineInput} from '@rive-app/react-canvas';
import {Button, Typography} from 'onboarding-components';

import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets';
import {track, TrackingEvent} from 'common/onboarding/immersive-onboarding/tracking';
import {useTranslation} from 'common/i18n/hooks';

import styles from './index.module.styl';

const openNewTabHandler = (appName: string) => {
  window.open(
    getReportUrl(appName, {projectId: getProjectId(), workspaceId: getWorkspaceId()}, true /** Ignore Search */),
    `_blank`,
  );
};

const ListeningContent = () => {
  const {t} = useTranslation();
  return (
    <div className={clsx(styles.content, styles.justifyContentCenter)}>
      <div className={clsx(styles.eventListenerContent, styles.justifyContentCenter)}>
        <Typography className={styles.eventListenerTitle} variant='bodySm' weight='bold'>
          {t(`onboarding.implement.listening-for-data`)}
        </Typography>
        <Typography className={styles.eventListenerDescription} variant='bodySm' weight='regular'>
          {t(`onboarding.implement.no-data-detected`)}
        </Typography>
      </div>
    </div>
  );
};

const ConnectedContent = () => {
  const {t} = useTranslation();
  return (
    <div className={clsx(styles.content, styles.fadeIn)}>
      <div className={clsx(styles.eventListenerContent)}>
        <Typography className={styles.eventListenerTitle} variant='bodySm' weight='bold'>
          <img src={IMMERSIVE_ASSETS.eventDetected} />
          {t(`onboarding.implement.data-detected`)}
        </Typography>
        <Typography className={styles.eventListenerDescription} variant='bodySm' weight='regular'>
          {t(`onboarding.implement.click-for-details`)}
        </Typography>
      </div>
      <div className={styles.eventListenerButtons}>
        <Button
          onClick={() => {
            track(TrackingEvent.ViewEventsClick);
            openNewTabHandler(`events`);
          }}
          variant={`transparent`}
          size={`small`}
        >
          {t(`onboarding.implement.view-events`)}
        </Button>
        <Button
          onClick={() => {
            track(TrackingEvent.ViewUsersClick);
            openNewTabHandler(`users`);
          }}
          variant={`transparent`}
          size={`small`}
        >
          {t(`onboarding.implement.view-users`)}
        </Button>
      </div>
    </div>
  );
};

const useLiveDataRive = () => {
  const stateMachineName = `State Machine - Live Data`;
  const stateMachineInputName = `Data Detected - Transition Trigger`;
  const stateMachineArtBoardName = `Live Data`;

  const {rive, RiveComponent}: RiveState = useRive({
    src: IMMERSIVE_ASSETS.eventListener,
    stateMachines: [stateMachineName],
    artboard: stateMachineArtBoardName,
    autoplay: true,
    layout: new Layout({fit: Fit.Cover, alignment: Alignment.Center}),
  });

  const transitionInput = useStateMachineInput(rive, stateMachineName, stateMachineInputName);

  return {RiveComponent, transitionInput};
};

export const EventListener = ({isIntegrated}) => {
  const {RiveComponent, transitionInput} = useLiveDataRive();

  React.useEffect(() => {
    if (isIntegrated) {
      transitionInput?.fire();
    }
  }, [isIntegrated, transitionInput]);

  return (
    <div className={clsx(styles.eventListenerContainer, !isIntegrated && styles.justifyContentCenter)}>
      {isIntegrated ? <ConnectedContent /> : <ListeningContent />}
      <RiveComponent className={styles.animationBorder} />
    </div>
  );
};
